<template>
  <CheckboxInput
    v-model="monthlyScoresStore.onlyKnownUsers"
    class="my-4 font-thin"
    :label="t('pages.wordle.only_known_users_checkbox')"
  ></CheckboxInput>
  <div class="flex items-center justify-between">
    <chevron-left-icon
      class="w-6 cursor-pointer text-gray-600 dark:text-white"
      @click="previousMonth"
    ></chevron-left-icon>
    <h1
      class="text-center text-lg font-medium text-kekkle-dark dark:text-white"
    >
      {{ t("pages.wordle.ranking_of") }} <br />
      <span class="font-bold text-kekkle-dark dark:text-white">
        {{ translatedMonths[monthlyScoresStore.selectedMonth - 1] }}
        {{ monthlyScoresStore.selectedYear }}</span
      >
    </h1>
    <chevron-right-icon
      class="w-6 cursor-pointer text-gray-600 dark:text-white"
      @click="nextMonth"
    ></chevron-right-icon>
  </div>

  <spinner
    v-if="fetchingScores && !orderedScores.length"
    class="mx-auto mt-16 w-8 animate-spin text-black-dark dark:text-white"
  ></spinner>

  <div
    v-else
    class="w-full rounded-sm border-2 border-black-dark bg-white p-4 text-kekkle-dark shadow-teal-hard-sm dark:bg-kekkle-dark-light dark:text-white"
  >
    <div>
      <div
        v-for="(score, index) in orderedScores"
        :key="score._id"
        :class="{
          'border-b border-black-dark':
            orderedScores[index + 1]?.new_score !== score.new_score,
        }"
        class="my-4"
      >
        <div class="flex items-center gap-4">
          <p class="text-3xl font-bold">
            <span
              v-if="authStore.authUuid === score.user_id"
              class="pointer text-xl"
              >👉</span
            >
            <span v-if="score.position === 1"> 🥇 </span>
            <span v-else-if="score.position === 2"> 🥈 </span>
            <span v-else-if="score.position === 3"> 🥉 </span>
            <span v-else> {{ score.position }}</span>
          </p>
          <div>
            <p class="">
              {{ t("pages.wordle.name") }}
              <span class="font-bold">{{ score.username }}</span>
            </p>
            <p v-if="groupMembers[score.user_id]" class="">
              {{ t("pages.wordle.nick_name") }}
              <span class="font-bold">{{
                groupMembers[score.user_id].name
              }}</span>
            </p>
            <p class="">
              {{ t("pages.wordle.score") }}
              <span class="font-bold">{{ score.new_score }}</span>
            </p>
            <div class="flex gap-4">
              <p class="">
                {{ t("pages.wordle.played") }}
                <span class="font-bold">{{ score.matches_played.length }}</span>
              </p>
              <p class="">
                {{ t("pages.wordle.wins") }}
                <span class="font-bold">{{ score.has_won }}</span>
              </p>
              <p class="">
                {{ t("pages.wordle.loses") }}
                <span class="font-bold">{{ score.has_lost }}</span>
              </p>
            </div>
          </div>
        </div>
        <matches-graph class="my-2" :matches="score.match_tries">
        </matches-graph>
      </div>

      <p v-if="!orderedScores.length">
        {{ t("pages.wordle.no_scores_known") }}
      </p>
    </div>
  </div>

  <DefaultButton
    v-if="monthlyScoresStore.hasMore && orderedScores.length"
    class="mt-4"
    :loading="fetchingScores"
    @click="loadMore"
  >
    {{ t("pages.wordle.load_more") }}
  </DefaultButton>
</template>
<script setup>
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/outline";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useWordleMonthlyScoresStore } from "@/stores/modules/wordle/wordle-monthly-scores";
import { useWordleScoresStore } from "@/stores/modules/wordle/wordle-scores";
import { useWordleLeaderboardStore } from "@/stores/modules/wordle/wordle-leaderboard";
import { computed, watch } from "vue";
import Spinner from "@/components/Spinner.vue";
import { useI18n } from "vue-i18n";
import CheckboxInput from "@/components/Inputs/CheckboxInput.vue";
import { months } from "@/i18n/vueI18n";
import MatchesGraph from "@/components/wordle/WordleMatchesGraph.vue";
import { useDebounceFn } from "@vueuse/core";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";

const { t } = useI18n({ useScope: "global" });

const translatedMonths = months();
const authStore = useAuthStore();
const groupsStore = useGroupsStore();
const monthlyScoresStore = useWordleMonthlyScoresStore();
const scoresStore = useWordleScoresStore();
const leaderboardStore = useWordleLeaderboardStore();

monthlyScoresStore.fetchMonthlyScores();

const decouncedFetchMonthlyScores = useDebounceFn(() => {
  monthlyScoresStore.fetchMonthlyScores();
  // do something
}, 250);

function nextMonth() {
  //add 1 to the month, unless it it twelve, set it back to 1 but the year to next year
  let currentMonth = monthlyScoresStore.selectedMonth;
  let currentYear = monthlyScoresStore.selectedYear;
  if (currentMonth === 12) {
    monthlyScoresStore.setSelectedMonth(1);
    monthlyScoresStore.setSelectedYear(currentYear + 1);
  } else {
    monthlyScoresStore.setSelectedMonth(currentMonth + 1);
  }
  decouncedFetchMonthlyScores();
}

function previousMonth() {
  //subtract 1 from the month, unless it is 1, set it to 12 but the year to the previous year
  let currentMonth = monthlyScoresStore.selectedMonth;
  let currentYear = monthlyScoresStore.selectedYear;
  if (currentMonth === 1) {
    monthlyScoresStore.setSelectedMonth(12);
    monthlyScoresStore.setSelectedYear(currentYear - 1);
  } else {
    monthlyScoresStore.setSelectedMonth(currentMonth - 1);
  }
  decouncedFetchMonthlyScores();
}

watch(
  () => monthlyScoresStore.onlyKnownUsers,
  (newValue) => {
    monthlyScoresStore.fetchMonthlyScores();
    leaderboardStore.setOnlyKnownUsersWorldeLeaderboard(newValue);
    scoresStore.setOnlyKnownUsersWordleScores(newValue);
    monthlyScoresStore.setOnlyKnownUsersWordleMonthlyScores(newValue);
  },
);

const orderedScores = computed(() => {
  let scores = monthlyScoresStore.currentMonthlyScores;
  let position = 0;
  let lastScore = null;

  scores.sort((a, b) => b.new_score - a.new_score);

  return scores.map((score) => {
    if (score.new_score !== lastScore) {
      position = position + 1;
      lastScore = score.new_score;
    }
    return {
      ...score,
      position: position,
    };
  });
});
const fetchingScores = computed(
  () => monthlyScoresStore.isFetchingMonthlyScores,
);
const groupMembers = computed(() => groupsStore.activeGroupMembers);

function loadMore() {
  monthlyScoresStore.fetchMonthlyScores(true);
}
</script>
<style>
.pointer {
  font-size: 28px;
}
</style>
